import React, { useEffect, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { graphql } from 'gatsby';
import sizes from 'react-sizes';
import {
  CaseStudyInfo,
  ContactUs,
  ProgramaticallyCreatedPageOverview,
  Hero,
  HeroImg,
  SimilarCards,
  SEO
} from 'components';

export default sizes(({ width }) => ({ viewportWidth: width && width }))(
  ({ data, location, handleHeader, handleEventManagementTrendsBannerLoading }) => {
    const { pathname } = location;
    const { caseStudy, similarCaseStudies } = data;
    const { html, frontmatter } = caseStudy;
    const { title, date, themes, summary, featuredImg, testimonial, carouselImgs } = frontmatter;

    useEffect(() => {
      const eventManagementBannerEC = document.querySelector('.event-management-banner-ec');
      if (eventManagementBannerEC) {
        handleEventManagementTrendsBannerLoading();
      }
    }, []);

    const containerRef = useRef(null);
    const { scrollYProgress } = useScroll({
      target: containerRef,
      offset: ['start end', 'end start']
    });

    const y = useTransform(scrollYProgress, [0, 0.1], [100, 0]);

    return (
      title && (
        <>
          <Hero
            title={title}
            subText={summary}
            date={date}
            themes={themes}
            handleHeader={handleHeader}>
            <HeroImg heroImg={featuredImg} />
          </Hero>
          <motion.div ref={containerRef} style={{ y }}>
            <ProgramaticallyCreatedPageOverview
              pathname={pathname}
              carouselImgs={carouselImgs && carouselImgs}>
              <CaseStudyInfo
                info={{
                  html,
                  testimonial
                }}
              />
            </ProgramaticallyCreatedPageOverview>
            <SimilarCards
              similarCards={similarCaseStudies.edges}
              linkPath="case-studies"
              pathname={pathname}
              paddingStyle="0 4.8%"
            />
          </motion.div>
          <ContactUs location={location} />
        </>
      )
    );
  }
);

export const Head = ({ data, location }) => {
  const { frontmatter } = data.caseStudy;
  return (
    <SEO
      pageSpecificTitle={
        frontmatter.metaTagOverrides?.title ? frontmatter.metaTagOverrides.title : frontmatter.title
      }
      pageSpecificDescription={
        frontmatter.metaTagOverrides?.description
          ? frontmatter.metaTagOverrides.description
          : frontmatter.summary
      }
      pageSpecificThumbnail={
        data.caseStudy.frontmatter.featuredImg.src.childImageSharp.gatsbyImageData.images.fallback
          .src
      }
      pageSpecificThumbnailAlt={data.caseStudy.frontmatter.featuredImg.src.name.replace(
        /-|_/g,
        ' '
      )}
      pathname={location.pathname}
    />
  );
};

export const query = graphql`
  query ($title: String!, $themes: [String!]!) {
    caseStudy: markdownRemark(frontmatter: { title: { eq: $title } }) {
      id
      html
      frontmatter {
        title
        summary
        metaTagOverrides {
          title
          description
        }
        themes
        date
        featuredImg {
          alt
          src {
            name
            childImageSharp {
              gatsbyImageData(
                width: 1440
                transformOptions: { cropFocus: CENTER }
                quality: 50
                pngOptions: { compressionSpeed: 10 }
                placeholder: BLURRED
                formats: [AUTO, WEBP]
              )
            }
          }
        }
        carouselImgs {
          alt
          src {
            childImageSharp {
              gatsbyImageData(
                width: 710
                height: 710
                transformOptions: { cropFocus: CENTER }
                quality: 65
                pngOptions: { compressionSpeed: 10 }
                placeholder: BLURRED
                formats: [AUTO, WEBP]
              )
            }
          }
        }
        testimonial {
          title
          companyLogo {
            childImageSharp {
              gatsbyImageData(
                width: 60
                height: 60
                transformOptions: { fit: CONTAIN }
                quality: 10
                backgroundColor: "transparent"
                pngOptions: { compressionSpeed: 10 }
                placeholder: BLURRED
                formats: [AUTO, WEBP]
              )
            }
          }
          personsName
          quote
        }
      }
    }
    similarCaseStudies: allFile(
      limit: 3
      filter: {
        childMarkdownRemark: { frontmatter: { title: { ne: $title }, themes: { in: $themes } } }
        sourceInstanceName: { eq: "cms_data_case_studies" }
        ext: { eq: ".md" }
      }
    ) {
      edges {
        node {
          childMarkdownRemark {
            frontmatter {
              title
              summary
              themes
              featuredImg {
                alt
                src {
                  childImageSharp {
                    gatsbyImageData(
                      width: 350
                      height: 200
                      transformOptions: { cropFocus: CENTER }
                      formats: [AUTO, WEBP]
                    )
                  }
                }
              }
              graphic {
                src {
                  childImageSharp {
                    gatsbyImageData(
                      width: 124
                      height: 79
                      transformOptions: { cropFocus: CENTER, fit: COVER }
                      formats: [AUTO, WEBP]
                    )
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
